import {parseDicom} from "dicom-parser";
import {CDUploadDataOverridingMetadata} from "../types/Session";

export type StudyRecap = CDUploadDataOverridingMetadata & {
	StudyDescription: string | undefined;
	StudyDate: string | undefined;
	Modality: string | undefined;

	numberOfImages: number,
	files: File[],
};

export type ParseProgress = {
	processed: number;
	discarded: number;
	total: number
}

type ParsedItem = {
	file: File;
	dataset: any; // dicom-parser dataset, no type available
};

export class Parser {
	static readonly SERIES_INSTANCE_UID: string = "0x0020000e";
	static readonly MEDIA_STORAGE_SOP_CLASS: string = "x00020002";
	static readonly STUDY_INSTANCE_UID: string = "x0020000d";
	static readonly TAG_PATIENT_NAME: string = "x00100010";
	static readonly TAG_PATIENT_ID: string = "x00100020";
	static readonly TAG_ISSUER_OF_PATIENT_ID: string = "x00100021";
	static readonly TAG_PATIENT_BIRTH_DATE: string = "x00100030";
	static readonly TAG_PATIENT_SEX: string = "x00100040";
	static readonly TAG_ACCESSION_NUMBER: string = "x00080050";
	static readonly TAG_STUDY_DESCRIPTION: string = "x00081030";
	static readonly TAG_STUDY_DATE: string = "x00080020";
	static readonly TAG_MODALITY: string = "x00080060";
	static readonly DICOMDIR_SOP_CLASS_UID = "1.2.840.10008.1.3.10";

	static excludedExtensions: Set<string> = new Set([
		"exe", "dll", "bat", "jar", "ini", "inf", "txt", "html", "xml",
		"zip", "ttf", "jpg", "jpeg", "png", "htm", "gif", "xsl", "css",
	]);

	static isValidExtension<T extends File>(file: T): boolean {
		const extension = file.name.toLowerCase().split(".").pop() ?? "";
		return !Parser.excludedExtensions.has(extension);
	}

	/**
	 * Async generator:
	 * - yields progress after every file: { processed, discarded, total }
	 * - finally returns the distinct patients extracted from the parsed datasets
	 */
	static async* parse(files: File[]): AsyncGenerator<
			ParseProgress,
			Map<string, StudyRecap>,
			void
	> {
		if (!files || files.length == 0) {
			return new Map<string, StudyRecap>();
		}

		const total = files.length;
		let processed = 0;
		let discarded = 0;

		const kept: ParsedItem[] = [];

		for (let i = 0; i < total; i++) {
			const file = files[i];
			try {
				if (!Parser.isValidExtension(file)) {
					discarded++;
					continue;
				}

				const buf = await file.arrayBuffer();
				const bytes = new Uint8Array(buf);
				const dataset = parseDicom(bytes, {untilTag: Parser.SERIES_INSTANCE_UID});

				const mediaStorageSopClass = dataset.string(Parser.MEDIA_STORAGE_SOP_CLASS);
				if (mediaStorageSopClass === Parser.DICOMDIR_SOP_CLASS_UID) {
					discarded++;
				} else {
					kept.push({file, dataset});
				}
			} catch (err) {
				console.error(`error reading file ${file.name}`, err)
				discarded++;
			} finally {
				processed++;
				yield {processed, discarded, total};
			}
		}

		return Parser.extractDistinctStudies(kept);
	}

	private static extractDistinctStudies(items: ParsedItem[]): Map<string, StudyRecap> {
		const studies = new Map<string, StudyRecap>();

		for (const {file, dataset} of items) {
			const studyInstanceUID = dataset.string(Parser.STUDY_INSTANCE_UID);

			if (!studies.has(studyInstanceUID)) {
				const patientName = dataset.string(Parser.TAG_PATIENT_NAME) ?? undefined;
				const patientID = dataset.string(Parser.TAG_PATIENT_ID) ?? undefined;
				const issuerOfPatientID = dataset.string(Parser.TAG_ISSUER_OF_PATIENT_ID) ?? undefined;
				const patientBirthDate = dataset.string(Parser.TAG_PATIENT_BIRTH_DATE) ?? undefined;
				const patientSex = dataset.string(Parser.TAG_PATIENT_SEX) ?? undefined;
				const accessionNumber = dataset.string(Parser.TAG_ACCESSION_NUMBER) ?? undefined;
				const studyDescription = dataset.string(Parser.TAG_STUDY_DESCRIPTION) ?? undefined;
				const studyDate = dataset.string(Parser.TAG_STUDY_DATE) ?? undefined;
				const modality = dataset.string(Parser.TAG_MODALITY) ?? undefined;

				const studyRecap: StudyRecap = {
					PatientName: patientName,
					PatientID: patientID,
					IssuerOfPatientID: issuerOfPatientID,
					PatientBirthDate: patientBirthDate,
					PatientSex: patientSex,
					AccessionNumber: accessionNumber,
					StudyDescription: studyDescription,
					StudyDate: studyDate,
					Modality: modality,
					numberOfImages: 0,
					files: []
				}
				studies.set(studyInstanceUID, studyRecap);
			}

			const currentStudy = studies.get(studyInstanceUID);
			if (currentStudy) {
				currentStudy.numberOfImages = currentStudy.numberOfImages + 1;
				currentStudy.files.push(file);
			}
		}

		return studies;
	}
}
